import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Building2,
  CheckCircle2,
  Clock,
  Eye,
  ExternalLink,
  FileText,
  Loader2,
  MapPin,
  Phone,
  Rocket,
  Sparkles,
  Upload,
  Users,
} from "lucide-react";
import { API } from "../Sidebar";
import { BlogPostUpload } from "./BlogPostUpload";
import { BlogAIGenerate } from "./BlogAIGenerate";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const GEO_API = `${API}/geo`;
const PREVIEW_URL = (path) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/geo/preview-by-path?path=${encodeURIComponent(path)}`;

const STAT_CARDS = [
  { key: "entities", label: "Entities", icon: Sparkles, color: "from-blue-500 to-blue-600" },
  { key: "planned", label: "Pages Planned", icon: FileText, color: "from-slate-500 to-slate-600" },
  { key: "published", label: "Published (WP)", icon: CheckCircle2, color: "from-emerald-500 to-emerald-600" },
  { key: "leads", label: "Leads Captured", icon: Users, color: "from-orange-500 to-orange-600" },
  { key: "ai_blocks", label: "AI Blocks Cached", icon: Rocket, color: "from-purple-500 to-purple-600" },
];

export const GeoDashboard = () => {
  const [batches, setBatches] = useState([]);
  const [plan, setPlan] = useState({ total: 0, by_batch: [] });
  const [wpPages, setWpPages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [aiBlockCount, setAiBlockCount] = useState(0);
  const [entityCount, setEntityCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [publishingBatch, setPublishingBatch] = useState(null);
  const [expandedBatch, setExpandedBatch] = useState(0);

  const loadAll = async () => {
    try {
      const [bRes, pRes, wpRes, lRes, aiRes, eRes] = await Promise.all([
        axios.get(`${GEO_API}/batches`),
        axios.get(`${GEO_API}/pages/plan`),
        axios.get(`${GEO_API}/wp/pages`),
        axios.get(`${GEO_API}/leads?limit=10`),
        axios.get(`${GEO_API}/ai/blocks`),
        axios.get(`${GEO_API}/entities`),
      ]);
      setBatches(bRes.data.items || []);
      setPlan(pRes.data || { total: 0, by_batch: [] });
      setWpPages(wpRes.data.items || []);
      setLeads(lRes.data.items || []);
      setAiBlockCount(aiRes.data.count || 0);
      setEntityCount((eRes.data.items || []).length);
    } catch (e) {
      toast.error("Failed to load GEO dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const wpPathSet = useMemo(() => new Set(wpPages.map((p) => p.path)), [wpPages]);

  const stats = {
    entities: entityCount,
    planned: plan.total,
    published: wpPages.filter((p) => p.publication_status === "published").length,
    leads: leads.length,
    ai_blocks: aiBlockCount,
  };

  const publishBatch = async (batchId, batchName) => {
    if (!window.confirm(`Publish Batch ${batchId} — "${batchName}" to WordPress?\n\nThis will create/update live pages on astarrestoration.com.`)) {
      return;
    }
    setPublishingBatch(batchId);
    try {
      const res = await axios.post(`${GEO_API}/wp/publish/batch`, {
        batch_id: batchId,
        dry_run: false,
      }, { timeout: 240000 });
      const { succeeded, total, failed } = res.data;
      if (failed === 0) {
        toast.success(`Batch ${batchId} published: ${succeeded}/${total} pages OK`);
      } else {
        toast.warning(`Batch ${batchId}: ${succeeded}/${total} OK, ${failed} failed`);
      }
      await loadAll();
    } catch (e) {
      toast.error(`Failed to publish Batch ${batchId}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setPublishingBatch(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="geo-dashboard-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="geo-dashboard" className="space-y-8 max-w-[1400px]">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: "var(--font-heading)" }}>
            <span className="text-orange-500 text-4xl">★</span>
            A-Star Restoration · GEO Dashboard
          </h2>
          <p className="text-slate-500 mt-1">
            Programmatic SEO MVP — 48 pages across 5 batches · Live on <a href="https://astarrestoration.com" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline inline-flex items-center gap-1">astarrestoration.com <ExternalLink className="w-3 h-3" /></a>
          </p>
        </div>
        <Button
          variant="outline"
          onClick={loadAll}
          className="text-sm"
          data-testid="geo-refresh-btn"
        >
          Refresh
        </Button>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="geo-stats-grid">
        {STAT_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.key} className="p-5 hover:shadow-md transition-shadow" data-testid={`geo-stat-${card.key}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{card.label}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">{stats[card.key]}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${card.color} text-white flex items-center justify-center`}>
                  <Icon className="w-5 h-5" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Blog upload */}
      <div>
        <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-slate-500" /> Blog — Upload &amp; Publish
        </h3>
        <Tabs defaultValue="upload" className="w-full" data-testid="blog-tabs">
          <TabsList className="mb-4" data-testid="blog-tabs-list">
            <TabsTrigger value="upload" data-testid="blog-tab-upload">Upload HTML</TabsTrigger>
            <TabsTrigger value="ai" data-testid="blog-tab-ai">Generate with AI</TabsTrigger>
          </TabsList>
          <TabsContent value="upload">
            <BlogPostUpload onPublished={loadAll} />
          </TabsContent>
          <TabsContent value="ai">
            <BlogAIGenerate onPublished={loadAll} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Batches */}
      <div>
        <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-slate-500" /> Publication Batches
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="geo-batches-grid">
          {batches.map((batch) => {
            const isCurrent = expandedBatch === batch.batch_id;
            const isPublishing = publishingBatch === batch.batch_id;
            const pct = batch.page_count > 0 ? Math.round((batch.published_pages / batch.page_count) * 100) : 0;
            const isComplete = pct === 100;
            return (
              <Card
                key={batch.batch_id}
                className={`p-5 transition-all cursor-pointer ${isCurrent ? "ring-2 ring-orange-500 shadow-md" : "hover:shadow-md"}`}
                onClick={() => setExpandedBatch(batch.batch_id)}
                data-testid={`geo-batch-card-${batch.batch_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Batch {batch.batch_id}</span>
                      <Badge variant="outline" className="text-[10px] font-medium">{batch.week}</Badge>
                    </div>
                    <h4 className="font-bold text-slate-900 text-base mt-1">{batch.name}</h4>
                  </div>
                  {isComplete ? (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Live
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0">
                      <Clock className="w-3 h-3 mr-1" /> Pending
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 leading-relaxed mb-4">{batch.focus}</p>

                {/* Progress bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="font-medium text-slate-700">{batch.published_pages} / {batch.page_count} pages</span>
                    <span className="text-slate-500">{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${isComplete ? "bg-emerald-500" : "bg-orange-500"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>

                <Button
                  size="sm"
                  variant={isComplete ? "outline" : "default"}
                  disabled={isPublishing}
                  onClick={(e) => {
                    e.stopPropagation();
                    publishBatch(batch.batch_id, batch.name);
                  }}
                  className={`w-full text-xs ${!isComplete ? "bg-orange-500 hover:bg-orange-600 text-white" : ""}`}
                  data-testid={`geo-publish-batch-${batch.batch_id}`}
                >
                  {isPublishing ? (
                    <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Publishing…</>
                  ) : (
                    <><Upload className="w-3 h-3 mr-2" /> {isComplete ? "Re-publish" : "Publish to WP"}</>
                  )}
                </Button>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Pages by batch */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-500" /> Pages · Batch {expandedBatch}
          </h3>
          <div className="flex gap-2">
            {plan.by_batch.map((b) => (
              <button
                key={b.batch}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${expandedBatch === b.batch ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                onClick={() => setExpandedBatch(b.batch)}
                data-testid={`geo-batch-tab-${b.batch}`}
              >
                B{b.batch} ({b.page_count})
              </button>
            ))}
          </div>
        </div>
        <Card className="overflow-hidden" data-testid="geo-pages-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Path</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Title</TableHead>
                <TableHead className="font-semibold text-center">WP Status</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(plan.by_batch.find((b) => b.batch === expandedBatch)?.pages || []).map((p, idx) => {
                const isLive = wpPathSet.has(p.path);
                return (
                  <TableRow key={p.path + idx} data-testid={`geo-page-row-${p.path}`}>
                    <TableCell className="font-mono text-xs text-slate-700 max-w-[260px] truncate">{p.path}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{p.page_type}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-700 max-w-[400px] truncate">{p.title || "—"}</TableCell>
                    <TableCell className="text-center">
                      {isLive ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0 text-[10px]">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Published
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500 text-[10px]">Planned</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <a
                          href={PREVIEW_URL(p.path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-xs text-slate-600 hover:text-orange-600 px-2 py-1 rounded hover:bg-slate-100"
                          data-testid={`geo-preview-${p.path}`}
                        >
                          <Eye className="w-3 h-3 mr-1" /> Preview
                        </a>
                        {isLive && (
                          <a
                            href={`https://astarrestoration.com${p.path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded hover:bg-emerald-50"
                            data-testid={`geo-live-${p.path}`}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" /> Live
                          </a>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Recent leads */}
      <div>
        <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-slate-500" /> Recent Leads
        </h3>
        {leads.length === 0 ? (
          <Card className="p-8 text-center text-slate-400 text-sm" data-testid="geo-leads-empty">
            No leads captured yet.
          </Card>
        ) : (
          <Card className="overflow-hidden" data-testid="geo-leads-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Time</TableHead>
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Phone</TableHead>
                  <TableHead className="font-semibold">City</TableHead>
                  <TableHead className="font-semibold">Service</TableHead>
                  <TableHead className="font-semibold">Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id} data-testid={`geo-lead-row-${lead.id}`}>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                      {new Date(lead.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                    </TableCell>
                    <TableCell className="font-medium text-slate-800">{lead.name}</TableCell>
                    <TableCell className="font-mono text-xs">
                      <a href={`tel:${lead.phone}`} className="text-orange-600 hover:underline inline-flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {lead.phone}
                      </a>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs"><MapPin className="w-3 h-3 mr-1" />{lead.city}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-600">{lead.service_slug}</TableCell>
                    <TableCell className="text-xs text-slate-400 font-mono truncate max-w-[180px]">{lead.source_path || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GeoDashboard;
